import React, { useMemo, useState } from "react";
import { getAvailability } from "../api";
import { errMsg, fmtMoney, fmtHour, bogotaToday, addDaysISO, AVAIL } from "../utils";

/** Franja de cuenta: empresa, modalidad y —solo para el administrador— saldos. */
export function AccountStrip({ me }) {
  if (!me) return null;
  const isAdmin = me.role === "admin";
  const modalidad = me.uses_credit ? "Con cupo de plata" : "Sin tope (se factura)";
  return (
    <div className="acct">
      <div className="acct-head">
        <div className="acct-name">{me.contract_name || me.company_name || "—"}</div>
        <span className="chip mode">{modalidad}</span>
      </div>
      {isAdmin ? (
        <>
          <div className="acct-nums">
            <div className="acct-num">
              <div className="lbl">Saldo disponible</div>
              <div className="val">{me.uses_credit ? fmtMoney(me.available) : "Sin tope"}</div>
            </div>
            <div className="acct-num">
              <div className="lbl">Apartado en cupones</div>
              <div className="val soft">{fmtMoney(me.committed_active)}</div>
            </div>
          </div>
          {me.max_daily_reserved_passengers != null && (
            <div className="acct-note">
              Tope diario: {me.max_daily_reserved_passengers} pasajeros reservados por día.
            </div>
          )}
        </>
      ) : (
        <div className="acct-note">
          Aquí creas cupones para tus pasajeros. El cupo y los movimientos de la
          empresa los gestiona el administrador.
        </div>
      )}
    </div>
  );
}

/** Inicio: franja de cuenta + búsqueda de horarios por fecha y tramo. */
export default function Home({ me, meError, onRetryMe, onCreate }) {
  const pairs = useMemo(() => me?.pairs || [], [me]);

  const today = bogotaToday();
  const maxDate = addDaysISO(today, 30);

  const [date, setDate] = useState(today);
  const [originId, setOriginId] = useState("");
  const [destId, setDestId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [results, setResults] = useState(null); // null = sin búsqueda aún
  const [searched, setSearched] = useState(null);

  const origins = useMemo(() => {
    const seen = new Map();
    pairs.forEach((p) => {
      if (!seen.has(String(p.origin_id))) seen.set(String(p.origin_id), p.origin_name);
    });
    return Array.from(seen, ([id, name]) => ({ id, name }));
  }, [pairs]);

  const dests = useMemo(
    () => pairs.filter((p) => String(p.origin_id) === String(originId)),
    [pairs, originId]
  );

  function changeOrigin(v) {
    setOriginId(v);
    const opts = pairs.filter((p) => String(p.origin_id) === String(v));
    // Si solo hay un destino posible, se preselecciona.
    setDestId(opts.length === 1 ? String(opts[0].destination_id) : "");
  }

  async function search(e) {
    e.preventDefault();
    setError("");
    if (!date || !originId || !destId) {
      setError("Elige fecha, origen y destino.");
      return;
    }
    if (date < today || date > maxDate) {
      setError("La fecha debe estar entre hoy y 30 días adelante.");
      return;
    }
    setLoading(true);
    setResults(null);
    try {
      const rows = await getAvailability(date, originId, destId);
      const pair = pairs.find(
        (p) =>
          String(p.origin_id) === String(originId) &&
          String(p.destination_id) === String(destId)
      );
      setSearched({
        date,
        originId,
        destId,
        originName: pair?.origin_name || "",
        destName: pair?.destination_name || "",
      });
      setResults(rows);
    } catch (e2) {
      setError(errMsg(e2, "No se pudo consultar la disponibilidad."));
    } finally {
      setLoading(false);
    }
  }

  if (meError) {
    return (
      <>
        <div className="banner err" style={{ marginTop: 16 }}>{meError}</div>
        <button className="btn" onClick={onRetryMe}>Reintentar</button>
      </>
    );
  }

  return (
    <>
      <AccountStrip me={me} />

      <div className="section">Buscar horarios</div>
      <form className="card" onSubmit={search}>
        <label>Fecha del viaje</label>
        <input
          type="date"
          value={date}
          min={today}
          max={maxDate}
          onChange={(e) => setDate(e.target.value)}
        />

        <label>Origen</label>
        <select value={originId} onChange={(e) => changeOrigin(e.target.value)}>
          <option value="">Elige el origen…</option>
          {origins.map((o) => (
            <option key={o.id} value={o.id}>{o.name}</option>
          ))}
        </select>

        <label>Destino</label>
        <select
          value={destId}
          onChange={(e) => setDestId(e.target.value)}
          disabled={!originId}
        >
          <option value="">{originId ? "Elige el destino…" : "Primero elige el origen"}</option>
          {dests.map((p) => (
            <option key={p.destination_id} value={p.destination_id}>
              {p.destination_name}
            </option>
          ))}
        </select>

        {pairs.length === 0 && (
          <div className="banner warn" style={{ marginTop: 14 }}>
            Tu convenio no tiene tramos autorizados. Comunícate con ALAS.
          </div>
        )}

        {error && <div className="banner err" style={{ marginTop: 14 }}>{error}</div>}

        <button className="btn" style={{ marginTop: 18 }} disabled={loading || pairs.length === 0}>
          {loading ? "Buscando…" : "Buscar"}
        </button>
      </form>

      {loading && <div className="center"><div className="spinner" /></div>}

      {!loading && results !== null && (
        <>
          <div className="section">
            Horarios del día
            <span className="count">{results.length}</span>
          </div>
          {results.length === 0 ? (
            <div className="card"><div className="empty">No hay salidas para esa fecha y tramo.</div></div>
          ) : (
            <>
              <div className="banner info">
                Estos horarios son <b>informativos</b>: te muestran las salidas del día en este
                tramo. El cupón <b>no reserva un horario ni una silla</b> — sirve para cualquier
                salida disponible ese día, y la taquilla asigna la silla al redimirlo.
              </div>

              <div className="card table-card">
                <div className="table-wrap">
                  <table className="tbl">
                    <thead>
                      <tr>
                        <th>Salida</th>
                        <th>Llegada</th>
                        <th>Servicio</th>
                        <th>Cupos</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.map((r, i) => {
                        const av = AVAIL[r.availability] || { label: r.availability || "—", cls: "" };
                        return (
                          <tr key={r.operation_id || i}>
                            <td><b>{fmtHour(r.departure_time)}</b></td>
                            <td>{fmtHour(r.arrival_time)}</td>
                            <td>{r.service_level || "—"}</td>
                            <td><span className={`chip ${av.cls}`}>{av.label}</span></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {(() => {
                const fareRow = results.find((r) => Number(r.fare) > 0) || results[0];
                const fare = Number(fareRow?.fare || 0);
                const allFull = results.every((r) => r.availability === "lleno");
                return (
                  <>
                    {allFull && (
                      <div className="banner warn">
                        Todas las salidas de hoy aparecen llenas. Puedes crear el cupón igual
                        (queda sujeto a disponibilidad), pero conviene revisar otra fecha.
                      </div>
                    )}
                    <div className="card">
                      <div className="tot-row">
                        <span>Tarifa del convenio por pasajero</span>
                        <b>{fmtMoney(fare)}</b>
                      </div>
                      <div className="tot-note">
                        El valor del cupón será esta tarifa × número de pasajeros.
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn accent"
                      onClick={() => onCreate({ ...searched, fare })}
                    >
                      Crear cupón para este tramo
                    </button>
                  </>
                );
              })()}
            </>
          )}
        </>
      )}
    </>
  );
}
